import SlideStateManager from './slide-state-manager.js';
import { createFrameUtils } from './utils/frame-utils.js';

export default class FrameEngine {
	#slideStateManager;
	ctx;

	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;

		_.#slideStateManager = new SlideStateManager(ctx);

		_.effect = null;
		_.rafId = null;

		_.handlers = {
			effectChanged: () => {
				_.effect = ctx.commands.getEffect();
				_.requestFrame();
			},
			storeDirty: () => {
				_.requestFrame();
			},
		};

		_.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		const { emitter, events } = this.ctx;

		// effect swapped
		emitter.on(events.effect.changed, this.handlers.effectChanged);

		// store changed → request frame
		emitter.on(events.store.changedDirty, this.handlers.storeDirty);
	}

	requestFrame() {
		if (this.rafId !== null) return; // already pending
		this.rafId = requestAnimationFrame(this.onFrame.bind(this));
	}

	cancel() {
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	}

	onFrame(time) {
		const _ = this;

		// clear rafId
		_.rafId = null;

		// Step 1: Tell SlideManager to prep slides (updates datastore)
		_.ctx.commands.getSlideManager().prepSlidesForFrame();

		// Step 2: Get fresh snapshot with prepped slides
		const snapshot = _.ctx.store.getSnapshot();

		// Sort slides by _renderIndex (important for transform calculations)
		// Create a copy for sorting since we shouldn't mutate the original array
		const sortedSlides = [...snapshot.slides].sort((a, b) => a._renderIndex - b._renderIndex);

		// Step 3: Build frame object with all data
		const frame = Object.freeze({
			state: snapshot.state, // all carousel state: selectedIndex, renderIndex, page, etc.
			widths: snapshot.widths, // all measured widths: viewport, slide, gap, etc.
			options: snapshot.options, // current active options in datastore
			slides: sortedSlides, // prepped slides array with calculated positions
			animation: snapshot.animation, // { movementType, trackPosition, trackDelta, velocity, progress }
			transformPoints: snapshot.transformPoints, // named position points for effects
			time: time, // current animation frame timestamp from requestAnimationFrame
		});

		// render frame
		_.renderFrame(frame);

		// mark store as clean after processing
		_.ctx.store.markAsClean();

		// schedule next frame only while an animation is in progress
		if (snapshot.animation.isAnimating) {
			// Emit event to tell track animator to continue physics calculations
			// The physics engine uses time delta between frames to calculate movement
			_.ctx.emitter.emit(_.ctx.events.track.requestFrame, { time });

			// request next frame
			_.requestFrame();
		}
	}

	renderFrame(frame) {
		const _ = this;

		// emit before render event
		_.ctx.emitter.emit(_.ctx.events.frame.beforeRender, frame);

		// pass frame and utils to effect.render(frame, utils)
		const utils = createFrameUtils(frame);
		_.effect.render(frame, utils);

		// update classes and ARIA states based on visibility in viewport
		_.#slideStateManager.updateSlides(frame.animation.trackPosition);

		// emit after render event
		_.ctx.emitter.emit(_.ctx.events.frame.afterRender, frame);
	}

	destroy() {
		const _ = this;
		const { emitter, events } = _.ctx;

		// Cancel any pending animation frame
		_.cancel();

		// Unbind events
		emitter.off(events.effect.changed, _.handlers.effectChanged);
		emitter.off(events.store.changedDirty, _.handlers.storeDirty);

		// Destroy slide state manager
		if (_.#slideStateManager) {
			_.#slideStateManager.destroy();
			_.#slideStateManager = null;
		}

		// Clear references
		_.effect = null;
		_.ctx = null;
	}
}
